/**
 * 采集调度模块（OFBiz 新接口）
 * ------------------------------------------------------------------
 * 对应后端：POST /itoss/control/json/collector/*
 *
 * 迁移自前端原接口（serverEquipment.js / src/api/index.js）：
 *   equipmentTestConnection → /rest/equipment/testconnection
 *   getResourceEquipmentModel → /rest/equipment/mib/getmodel
 *   getDiskspaceNamesList 等 → /rest/equipment/{xxx}/names
 */
import { ofbiz } from './http';

/**
 * 测试设备连接（替代旧 /rest/equipment/testconnection）
 *
 * 后端优先 SNMP(get sysDescr)，失败回退 TCP 端口探测(161/22/80/443)。
 * @param {object} opts
 * @param {string} [opts.host] 目标地址（与 equipmentId 二选一）
 * @param {string} [opts.equipmentId] 设备 ID（自动取 IP_ADDRESS）
 * @param {string} [opts.community] SNMP community，默认 public
 * @param {string|number} [opts.port] SNMP 端口，默认 161
 * @returns {Promise<object>} 连接结果
 */
export function testConnection({ host, equipmentId, community, port, protocol, user, password } = {}) {
  // silent=true：业务错误（如参数缺失）不弹通用提示，由 action 层显示后端返回的 message
  return ofbiz.post('collector/testConnection', null, {
    host,
    equipmentId,
    community,
    port,
    protocol,
    user,
    password,
  }, { silent: true });
}

/**
 * 识别资源型号（替代旧 /rest/equipment/mib/getmodel）
 * @param {object} opts { host, equipmentId, community, port }
 * @returns {Promise<object>}
 */
export function getModel({ host, equipmentId, community, port } = {}) {
  return ofbiz.post('collector/getModel', null, {
    host,
    equipmentId,
    community,
    port,
  });
}

/**
 * 设备能力发现（替代旧 /rest/equipment/{type}/names）
 * 统一入口，后端按 resourceType 走 SNMP walk HOST-RESOURCES-MIB / IF-MIB
 * @param {string} resourceType diskspace | disksio | disksinodes | harddisk | process | network | service | script
 * @param {object} opts { equipmentId, host, community, port, monitorType, path }
 * @returns {Promise<object>}
 */
export function discover(resourceType, { equipmentId, host, community, port, monitorType, path } = {}) {
  return ofbiz.post('collector/discover', { resourceType }, {
    equipmentId,
    host,
    community,
    port,
    monitorType,
    path,
  }, { silent: true });
}

/**
 * MIB 文件列表（替代旧 /rest/equipment/bymib/names）
 * @returns {Promise<array>}
 */
export function listMibFiles() {
  return ofbiz.post('collector/listMibFiles');
}

/**
 * MIB 计数器列表（替代旧 /rest/equipment/bymib/counters）
 * @param {string} fileName MIB 文件名
 * @returns {Promise<array>}
 */
export function listMibCounters(fileName) {
  return ofbiz.post('collector/listMibCounters', null, { fileName });
}

/**
 * MySQL 计数器列表（替代旧 /rest/equipment/mysql/counters）
 * @param {string} equipmentId
 * @returns {Promise<array>}
 */
export function listMysqlCounters(equipmentId) {
  return ofbiz.post('collector/listMysqlCounters', null, { equipmentId });
}

/**
 * VMware 资源发现（替代旧 /rest/equipment/vmware/names）
 * @param {object} opts { equipmentId, monitorType }
 * @returns {Promise<object>}
 */
export function discoverVMware({ equipmentId, monitorType } = {}) {
  return ofbiz.post('collector/discoverVMware', null, {
    equipmentId,
    monitorType,
  }, { silent: true });
}
